import React from 'react';
import Text from '@amzn/meridian/text';
import DisplaySubMetric from './DisplaySubMetric';
import Row from '@amzn/meridian/row';
import { FormattedMessage } from 'react-intl';
import { DisplaySubMetricsProps } from '../types';
import {
  SUBMETRIC_FONT_SIZE,
  SUBMETRIC_ROW_WIDTHS,
  TRDataCheck,
} from '../helper';
import { ControlledExpander } from './Util/ControlledExpander';
import Column from '@amzn/meridian/column';
import NoSubMetric from './Util/NoSubMetric';

const DisplayTrSubMetrics: React.FC<DisplaySubMetricsProps> = ({
  metrics,
}: DisplaySubMetricsProps): React.ReactElement => {
  if (!metrics.TR?.submetrics) {
    return <></>;
  }
  if (TRDataCheck(metrics.TR?.submetrics!)) {
    return <NoSubMetric />;
  }
  const {
    transferredContacts,
    muParkedRate,
    handledContacts,
    boomerangContactCount,
    consults,
    resolvedContacts,
    escalatedContacts,
    resolveRate,
    lockToResolve,
  } = metrics.TR?.submetrics!;

  return (
    <Column spacing='none' width='100%'>
      {boomerangContactCount && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_boomerangContacts'
                  defaultMessage='Boomerang Contacts'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_boomerangContacts_def'
                defaultMessage='Sum of transferred contacts which were eventually resolved in the same skill.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={boomerangContactCount.datapoint} isBold />
          <DisplaySubMetric
            value={boomerangContactCount.networkAverage}
            datapointNull={boomerangContactCount.datapoint === null}
          />
        </Row>
      )}
      {consults && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_consults'
                  defaultMessage='Consults'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_consults_def'
                defaultMessage='Percentage of consult contacts out of total handled contacts.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={consults.datapoint} isBold />
          <DisplaySubMetric
            value={consults.networkAverage}
            datapointNull={consults.datapoint === null}
          />
        </Row>
      )}

      {escalatedContacts && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_escalatedContacts'
                  defaultMessage='Escalated Contacts'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_escalatedContacts_def'
                defaultMessage='Sum of handled contacts which resulted in an escalation.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={escalatedContacts.datapoint} isBold />
          <DisplaySubMetric
            value={escalatedContacts.networkAverage}
            datapointNull={escalatedContacts.datapoint === null}
          />
        </Row>
      )}

      {handledContacts && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_handledContacts'
                  defaultMessage='Handled Contacts'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_handledContacts_def'
                defaultMessage="Sum of contacts considered 'Handled'."
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={handledContacts.datapoint} isBold />
          <DisplaySubMetric
            value={handledContacts.networkAverage}
            datapointNull={handledContacts.datapoint === null}
          />
        </Row>
      )}

      {lockToResolve && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_lockToResolve'
                  defaultMessage='Lock to Resolve (LTR)'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_lockToResolve_def'
                defaultMessage='Average time between the email being locked to being resolved.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric
            value={lockToResolve.datapoint}
            isTime
            isBold
            isHour
          />
          <DisplaySubMetric
            value={lockToResolve.networkAverage}
            isTime
            isHour
            datapointNull={lockToResolve.datapoint === null}
          />
        </Row>
      )}

      {muParkedRate && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_muParkedRate'
                  defaultMessage='Message Us Parked Rate'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_muParkedRate_def'
                defaultMessage='Percentage of parked contacts out of total Message Us handled contacts.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric
            value={muParkedRate.datapoint}
            isPercentage
            isBold
          />
          <DisplaySubMetric
            value={muParkedRate.networkAverage}
            isPercentage
            datapointNull={muParkedRate.datapoint === null}
          />
        </Row>
      )}

      {resolveRate && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_resolveRate'
                  defaultMessage='Resolve Rate'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_resolveRate_def'
                defaultMessage='Percentage of handled contacts which were resolved by the associate.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={resolveRate.datapoint} isBold isPercentage />
          <DisplaySubMetric
            value={resolveRate.networkAverage}
            isPercentage
            datapointNull={resolveRate.datapoint === null}
          />
        </Row>
      )}

      {resolvedContacts && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_resolvedContacts'
                  defaultMessage='Resolved Contacts'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_resolvedContacts_def'
                defaultMessage='Sum of contacts which are considered resolved.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={resolvedContacts.datapoint} isBold />
          <DisplaySubMetric
            value={resolvedContacts.networkAverage}
            datapointNull={resolvedContacts.datapoint === null}
          />
        </Row>
      )}

      {transferredContacts && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_transferredContacts'
                  defaultMessage='Transferred Contacts'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_transferredContacts_def'
                defaultMessage='Sum of handled contacts which resulted in a transfer.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric value={transferredContacts.datapoint} isBold />
          <DisplaySubMetric
            value={transferredContacts.networkAverage}
            datapointNull={transferredContacts.datapoint === null}
          />
        </Row>
      )}
    </Column>
  );
};

export default DisplayTrSubMetrics;
